import { useAuth } from '../auth/useAuth';
import { useLocation } from 'preact-iso';
/**
 * Hook to access complete user profile data including company roles and workspace roles
 */
export const useUserProfile = () => {
    const { userProfile, userCompanyRoleSets, allWorkspaces, homeClientCompanyId, canManageCustomers } = useAuth();
    const location = useLocation();
    // Extract current company ID from URL
    const getCurrentCompanyId = () => {
        try {
            const match = location?.path?.match(/\/company\/([^/]+)/);
            return match?.[1] || null;
        }
        catch {
            return null;
        }
    };
    // Get role set for current company from URL
    const getCurrentCompanyRoleSet = () => {
        const currentCompanyId = getCurrentCompanyId();
        if (!currentCompanyId || !userCompanyRoleSets)
            return null;
        return userCompanyRoleSets.find(rs => rs.ClientCompanyId === currentCompanyId) || null;
    };
    // Get workspaces for current company
    const getCurrentCompanyWorkspaces = () => {
        const currentCompanyId = getCurrentCompanyId();
        if (!currentCompanyId || !allWorkspaces)
            return [];
        return allWorkspaces.filter(w => w.ClientCompanyId === currentCompanyId);
    };
    // Check if user has a specific role (company-wide or in a workspace)
    const isUserInRole = (role, companyId, workspaceId) => {
        const roleSet = userCompanyRoleSets?.find(rs => rs.ClientCompanyId === companyId);
        if (!roleSet)
            return false;
        // Check company-wide roles
        if (!workspaceId) {
            return roleSet.CompanyRoles.includes(role);
        }
        // Check workspace-specific roles
        const workspaceRoles = roleSet.WorkspaceRoles.find((wr) => wr.WorkspaceId === workspaceId);
        return workspaceRoles?.Roles.includes(role) || false;
    };
    // Get all roles for user (optionally filtered by workspace)
    const getUserRoles = (companyId, workspaceId) => {
        const roleSet = userCompanyRoleSets?.find(rs => rs.ClientCompanyId === companyId);
        if (!roleSet)
            return [];
        if (!workspaceId) {
            // Return company-wide roles
            return roleSet.CompanyRoles;
        }
        // Return workspace-specific roles
        const workspaceRoles = roleSet.WorkspaceRoles.find((wr) => wr.WorkspaceId === workspaceId);
        return workspaceRoles?.Roles || [];
    };
    // Check if user has a company-wide role
    const hasCompanyRole = (role, companyId) => {
        const roleSet = userCompanyRoleSets?.find(rs => rs.ClientCompanyId === companyId);
        return roleSet?.CompanyRoles.includes(role) || false;
    };
    // Check if user has a role in a specific workspace
    const hasWorkspaceRole = (role, workspaceId, companyId) => {
        const roleSet = userCompanyRoleSets?.find(rs => rs.ClientCompanyId === companyId);
        if (!roleSet)
            return false;
        const workspaceRoles = roleSet.WorkspaceRoles.find((wr) => wr.WorkspaceId === workspaceId);
        return workspaceRoles?.Roles.includes(role) || false;
    };
    // Get workspace by ID
    const getWorkspaceById = (workspaceId) => {
        return allWorkspaces?.find(ws => ws.Id === workspaceId);
    };
    // Get all workspaces the user has roles in for a specific company
    const getUserWorkspaces = (companyId) => {
        const roleSet = userCompanyRoleSets?.find(rs => rs.ClientCompanyId === companyId);
        if (!roleSet || !allWorkspaces)
            return [];
        const userWorkspaceIds = roleSet.WorkspaceRoles.map((wr) => wr.WorkspaceId);
        return allWorkspaces.filter(ws => ws.ClientCompanyId === companyId && userWorkspaceIds.includes(ws.Id));
    };
    // Get workspace capabilities (two-tier: company admin first, then workspace-specific flags)
    const getWorkspaceCapabilities = (clientCompanyId, workspaceId) => {
        const roleSet = userCompanyRoleSets?.find(rs => rs.ClientCompanyId === clientCompanyId);
        if (!roleSet)
            return { canAdmin: false, canEdit: false, canView: false };
        // First tier: Check if user is company-level admin
        if (roleSet.IsAdmin) {
            return { canAdmin: true, canEdit: true, canView: true };
        }
        // Second tier: Check workspace-specific roles (server-computed flags)
        const workspaceRoles = roleSet.WorkspaceRoles.find((wr) => wr.WorkspaceId === workspaceId);
        return {
            canAdmin: workspaceRoles?.CanAdmin || false,
            canEdit: workspaceRoles?.CanEdit || false,
            canView: workspaceRoles?.CanView || false
        };
    };
    return {
        userProfile,
        userCompanyRoleSets,
        allWorkspaces,
        homeClientCompanyId,
        canManageCustomers,
        getCurrentCompanyRoleSet,
        getCurrentCompanyWorkspaces,
        isUserInRole,
        getUserRoles,
        hasCompanyRole,
        hasWorkspaceRole,
        getWorkspaceCapabilities,
        getWorkspaceById,
        getUserWorkspaces,
    };
};
// Common role constants you can use throughout your app
export const USER_ROLES = {
    // Available role types from backend
    VIEWER: 'Viewer',
    EDITOR: 'Editor',
    ADMIN: 'Admin',
};
